import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MovingAverageChart = ({ groupedData, days = 7, title = 'Moving Average' }) => {
  // Calculate moving average from the grouped data
  const { labels, averageData } = useMemo(() => {
    // If groupedData is empty or not an object, return empty arrays
    if (!groupedData || typeof groupedData !== 'object' || Object.keys(groupedData).length === 0) {
      return { labels: [], averageData: [] };
    }

    // Convert object to array of [date, duration] pairs and sort by date
    const dataArray = Object.entries(groupedData).map(([date, duration]) => {
      return { date, duration: Number(duration) };
    }).sort((a, b) => moment(a.date, 'DD/MM/YYYY').valueOf() - moment(b.date, 'DD/MM/YYYY').valueOf());

    const sortedLabels = dataArray.map(item => item.date);
    const sortedData = dataArray.map(item => item.duration);
    
    // Calculate moving average
    const movingAvg = [];
    
    for (let i = 0; i < sortedData.length; i++) {
      // Calculate how many days to look back (can't look back before start of array)
      const lookBack = Math.min(i, days - 1);
      let sum = 0;
      let count = 0;
      
      // Sum up the last 'days' entries (or as many as available)
      for (let j = i - lookBack; j <= i; j++) {
        sum += sortedData[j];
        count++;
      }
      
      // Calculate average (convert from minutes to hours)
      movingAvg.push((sum / count) / 60);
    }
    
    return { labels: sortedLabels, averageData: movingAvg };
  }, [groupedData, days]);

  const chartData = {
    labels,
    datasets: [
      {
        label: `${days}-Day Moving Average`,
        data: averageData,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
      }
    ]
  };

  const options = {
    responsive: true,
    color: 'white',
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'white'
        }
      },
      title: {
        display: true,
        text: title,
        color: 'white'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const hours = context.parsed.y.toFixed(1);
            return `Average: ${hours} ${hours === "1.0" ? "hour" : "hours"}`;
          }
        },
        titleColor: 'white',
        bodyColor: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Duration (hours)',
          color: 'white'
        },
        ticks: {
          callback: (value) => {
            return value.toFixed(1);
          },
          color: 'white'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date',
          color: 'white'
        },
        ticks: {
          color: 'white'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    },
  };

  // If no data, show a message
  if (labels.length === 0) {
    return <div style={{ textAlign: 'center', padding: '20px', color: 'white' }}>No data available for moving average</div>;
  }

  return <Line data={chartData} options={options} />;
};

export default MovingAverageChart; 